/// Port of mobile/lib/user/ui/user_screen.dart.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';

import { appRoutes } from '@/app/router/routes';
import { useAuthStore } from '@/auth/handler/authStore';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelActionButton, PixelLinkButton } from '@/ui/pixel/PixelActionButton';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelIcon } from '@/ui/pixel/PixelIcon';
import type { User } from '@/user/domain/user';

function canManagePassword(user: User): boolean {
  if (user.isAnonymous) return false;
  if (user.email.length === 0 || user.email.endsWith('.placeholder')) return false;
  return true;
}

function needsEmailVerification(user: User): boolean {
  if (user.isAnonymous || user.emailVerified) return false;
  if (user.email.length === 0 || user.email.endsWith('.placeholder')) return false;
  return user.hasPassword;
}

function hasSocialSignIn(user: User): boolean {
  return user.providers.includes('google') || user.providers.includes('apple');
}

function SignInMethodChip({
  iconSrc,
  label,
}: {
  iconSrc: string;
  label: string;
}) {
  const colors = useThemeColors();

  return (
    <PixelContainer
      radius={3}
      pixelScale={2}
      padding="10px 12px"
      backgroundColor={colors.surfaceContainerHighest}
      borderColor={colors.onSurface}
      className="w-full"
    >
      <div className="flex items-center gap-2.5">
        <img src={iconSrc} alt="" width={22} height={22} className="shrink-0" />
        <span className="leading-snug" style={{ fontSize: 20, color: colors.onSurface }}>
          {label}
        </span>
      </div>
    </PixelContainer>
  );
}

export function UserScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const resendVerification = useAuthStore((s) => s.resendVerification);
  const verifyOtpForUser = useAuthStore((s) => s.verifyOtpForUser);
  const isRestoring = useAuthStore((s) => s.isRestoring);

  const [otpCode, setOtpCode] = useState('');
  const [resendSeconds, setResendSeconds] = useState(0);
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  if (!isRestoring && user.isAnonymous) {
    return <Navigate to={appRoutes.auth} replace />;
  }

  const onResend = async (): Promise<void> => {
    if (resendSeconds > 0 || resending) return;
    setVerifyMessage(null);
    setResending(true);
    try {
      await resendVerification();
      setResendSeconds(60);
      setVerifyMessage(t('authOtpResend'));
    } catch (error) {
      setVerifyMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setResending(false);
    }
  };

  const onVerify = async (codeOverride?: string): Promise<void> => {
    const code = (codeOverride ?? otpCode).trim();
    if (code.length !== 4) {
      setVerifyMessage(t('authOtpInvalid'));
      return;
    }
    setVerifyMessage(null);
    setVerifying(true);
    try {
      await verifyOtpForUser(code);
      setVerifyMessage(t('authVerifySuccess'));
      setOtpCode('');
    } catch (error) {
      setVerifyMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setVerifying(false);
    }
  };

  const onOtpChange = (value: string): void => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    setOtpCode(digits);
    if (digits.length === 4 && !verifying) {
      void onVerify(digits);
    }
  };

  return (
    <PageChrome title={t('authProfileTitle')}>
      <div className="flex-1 overflow-y-auto px-6 pb-10 pt-6">
        <div className="leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
          {t('authUsername')}
        </div>
        <PixelContainer
          radius={3}
          pixelScale={2}
          padding="10px 12px"
          backgroundColor={colors.surfaceContainerHighest}
          borderColor={colors.onSurface}
          className="mt-2 w-full"
          onClick={() => navigate(appRoutes.changeUsername)}
        >
          <div className="flex items-center gap-3">
            <span
              className="min-w-0 flex-1 truncate leading-snug"
              style={{ fontSize: 22, color: colors.onSurface }}
            >
              {user.username}
            </span>
            <PixelIcon name="edit" set="pixel_icons" size={22} color={colors.onSurface} />
          </div>
        </PixelContainer>

        <div className="mt-7 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
          {t('authEmail')}
        </div>
        <PixelContainer
          radius={3}
          pixelScale={2}
          padding="10px 12px"
          backgroundColor={colors.surfaceContainerHighest}
          borderColor={colors.onSurface}
          className="mt-2 w-full"
          onClick={() => navigate(appRoutes.changeEmail)}
        >
          <div className="flex items-center gap-3">
            <span
              className="min-w-0 flex-1 truncate leading-snug"
              style={{ fontSize: 22, color: colors.onSurface }}
            >
              {user.email}
            </span>
            <PixelIcon name="edit" set="pixel_icons" size={22} color={colors.onSurface} />
          </div>
        </PixelContainer>

        {needsEmailVerification(user) && (
          <>
            <p
              className="mt-4 leading-tight"
              style={{ fontSize: 16, color: colors.onSurface, opacity: 0.75 }}
            >
              {t('authVerifyPending', { email: user.email })}
            </p>
            {verifyMessage && (
              <p className="mt-2 leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
                {verifyMessage}
              </p>
            )}
            <PixelContainer
              radius={3}
              pixelScale={2}
              padding="4px 12px"
              backgroundColor={colors.surfaceContainerHighest}
              borderColor={colors.onSurface}
              className="mt-3 w-full"
            >
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                value={otpCode}
                disabled={verifying}
                onChange={(e) => onOtpChange(e.target.value)}
                className="w-full bg-transparent outline-none"
                style={{ fontSize: 22, color: colors.onSurface, padding: '6px 0' }}
              />
            </PixelContainer>
            <PixelActionButton
              label={t('authContinue')}
              busy={verifying}
              className="mt-3"
              onClick={verifying ? undefined : () => void onVerify()}
            />
            <PixelLinkButton
              label={resendSeconds > 0 ? t('authOtpResendWait', { seconds: resendSeconds }) : t('authOtpResend')}
              busy={resending}
              disabled={resending || resendSeconds > 0}
              onClick={() => void onResend()}
            />
          </>
        )}

        {hasSocialSignIn(user) && (
          <>
            <div className="mt-7 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
              {t('profileSignInMethods')}
            </div>
            <div className="mt-2 flex flex-col gap-2">
              {user.providers.includes('google') && (
                <SignInMethodChip
                  iconSrc="/assets/icons/google.svg"
                  label={t('profileSignedInWithGoogle')}
                />
              )}
              {user.providers.includes('apple') && (
                <SignInMethodChip
                  iconSrc="/assets/icons/apple.svg"
                  label={t('profileSignedInWithApple')}
                />
              )}
            </div>
          </>
        )}

        {canManagePassword(user) && (
          <>
            <div className="mt-7 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
              {t('authPassword')}
            </div>
            <PixelContainer
              radius={3}
              pixelScale={2}
              padding="10px 12px"
              backgroundColor={colors.surfaceContainerHighest}
              borderColor={colors.onSurface}
              className="mt-2 w-full"
              onClick={() => navigate(appRoutes.changePassword)}
            >
              <div className="flex items-center gap-3">
                <span
                  className="min-w-0 flex-1 leading-snug"
                  style={{ fontSize: 22, color: colors.onSurface }}
                >
                  {user.hasPassword ? t('profileChangePasswordTitle') : t('profileAddPassword')}
                </span>
                <PixelIcon name="edit" set="pixel_icons" size={22} color={colors.onSurface} />
              </div>
            </PixelContainer>
            {!user.hasPassword && (
              <p className="mt-2 leading-tight" style={{ fontSize: 14, color: colors.onSurface, opacity: 0.65 }}>
                {t('profileAddPasswordHint')}
              </p>
            )}
          </>
        )}

        <PixelContainer
          radius={3}
          pixelScale={2}
          padding="12px 0"
          backgroundColor={colors.surfaceContainerHighest}
          borderColor={colors.onSurface}
          className="mt-8 w-full"
          onClick={() => {
            void signOut().then(() => navigate(-1));
          }}
        >
          <div className="flex justify-center">
            <span className="leading-none" style={{ fontSize: 22, color: colors.onSurface }}>
              {t('authSignOut')}
            </span>
          </div>
        </PixelContainer>
      </div>
    </PageChrome>
  );
}
